import {StateCreator} from "zustand/vanilla"
import {Release} from "@/pb/api/devplan/types/documents"
import {
  ArchiveReleaseRequest,
  ArchiveReleaseResponse,
  CreateReleaseRequest,
  CreateReleaseResponse,
  GetReleasesResponse,
  ReleaseProjectOrderUpdate,
  ReleaseTargetDateUpdate,
  UpdateReleaseRequest,
  UpdateReleaseResponse,
} from "@/pb/api/devplan/services/web/company"
import {releasesAPI} from "@/app/apiPaths"

/**
 * Partial-update shape for `updateRelease`. Only fields explicitly set on the
 * object affect the stored release; omitted fields are left untouched.
 *
 * - `targetDate`: pass a `Date` to set, `null` to clear. Omit to leave alone.
 * - `projectOrder`: pass an array to replace the stored order (use `[]` to
 *   clear). Omit to leave alone.
 */
export type ReleaseUpdate = {
  id: string
  name?: string
  archived?: boolean
  targetDate?: Date | null
  projectOrder?: string[]
}

export type ReleasesStoreState = {
  releases: Record<number, Release[]>
  fetchReleases(companyId: number): Promise<void>
  createRelease(companyId: number, release: Release): Promise<Release>
  updateRelease(companyId: number, update: ReleaseUpdate): Promise<Release>
  setReleaseArchived(companyId: number, releaseId: string, archived: boolean): Promise<Release>
}

function applyOptimistic(release: Release, update: ReleaseUpdate): Release {
  const next = {...release}
  if (update.name !== undefined) next.name = update.name
  if (update.archived !== undefined) next.archived = update.archived
  if ("targetDate" in update) next.targetDate = update.targetDate ?? undefined
  if (update.projectOrder !== undefined) next.projectOrder = update.projectOrder
  return next
}

function toRequest(update: ReleaseUpdate): UpdateReleaseRequest {
  return {
    id: update.id,
    name: update.name,
    archived: update.archived,
    targetDate: "targetDate" in update
      ? ReleaseTargetDateUpdate.create({value: update.targetDate ?? undefined})
      : undefined,
    projectOrder: update.projectOrder !== undefined
      ? ReleaseProjectOrderUpdate.create({projectIds: update.projectOrder})
      : undefined,
  }
}

export const createReleasesSlice: StateCreator<
  ReleasesStoreState,
  [],
  [],
  ReleasesStoreState
> = ((set) => ({
  releases: {},
  fetchReleases: async (companyId) => {
    const response = await fetch(releasesAPI(companyId))
    if (!response.ok) {
      throw new Error(response.statusText)
    }
    const js = await response.json()
    const {releases} = GetReleasesResponse.fromJSON(js)
    const sorted = sortReleases(releases || [])
    set(s => ({
      ...s,
      releases: {...s.releases, [companyId]: sorted},
    }))
  },
  createRelease: async (companyId, release) => {
    const response = await fetch(releasesAPI(companyId), {
      method: "POST",
      body: JSON.stringify(CreateReleaseRequest.toJSON({release}))
    })
    const js = await readJsonOrThrow(response)
    const {release: created} = CreateReleaseResponse.fromJSON(js)
    set(s => {
      const companyReleases = s.releases[companyId] || []
      return {
        ...s,
        releases: {...s.releases, [companyId]: sortReleases([...companyReleases, created!])}
      }
    })
    return created!
  },
  updateRelease: async (companyId, update) => {
    // Apply the change locally before awaiting the PUT so consumers (e.g.
    // by-release roadmap drag) see the new ordering immediately and the row
    // doesn't snap back to its old position between drop and server response.
    // Snapshot the prior state in case the request fails and we need to revert.
    let previous: Release | undefined
    set(s => {
      const companyReleases = s.releases[companyId] || []
      const existingIndex = companyReleases.findIndex(r => r.id === update.id)
      if (existingIndex < 0) {
        return s
      }
      previous = companyReleases[existingIndex]
      const optimistic = applyOptimistic(previous, update)
      const next = companyReleases.map((r, i) => i === existingIndex ? optimistic : r)
      return {
        ...s,
        releases: {...s.releases, [companyId]: sortReleases(next)}
      }
    })
    try {
      const response = await fetch(releasesAPI(companyId), {
        method: "PUT",
        body: JSON.stringify(UpdateReleaseRequest.toJSON(toRequest(update)))
      })
      const js = await readJsonOrThrow(response)
      const {release: updated} = UpdateReleaseResponse.fromJSON(js)
      set(s => {
        const companyReleases = s.releases[companyId] || []
        const existingIndex = companyReleases.findIndex(r => r.id === updated!.id)
        const next = existingIndex >= 0
          ? companyReleases.map((r, i) => i === existingIndex ? updated! : r)
          : [...companyReleases, updated!]
        return {
          ...s,
          releases: {...s.releases, [companyId]: sortReleases(next)}
        }
      })
      return updated!
    } catch (e) {
      // Revert the optimistic update so the UI returns to its previous state.
      if (previous) {
        const restored = previous
        set(s => {
          const companyReleases = s.releases[companyId] || []
          const existingIndex = companyReleases.findIndex(r => r.id === update.id)
          const next = existingIndex >= 0
            ? companyReleases.map((r, i) => i === existingIndex ? restored : r)
            : companyReleases
          return {
            ...s,
            releases: {...s.releases, [companyId]: sortReleases(next)}
          }
        })
      }
      throw e
    }
  },
  setReleaseArchived: async (companyId, releaseId, archived) => {
    const response = await fetch(releasesAPI(companyId), {
      method: "PATCH",
      body: JSON.stringify(ArchiveReleaseRequest.toJSON({releaseId, archived}))
    })
    const js = await readJsonOrThrow(response)
    const {release: updated} = ArchiveReleaseResponse.fromJSON(js)
    set(s => {
      const companyReleases = s.releases[companyId] || []
      const updatedReleases = companyReleases.map(r => r.id === updated!.id ? updated! : r)
      return {
        ...s,
        releases: {...s.releases, [companyId]: sortReleases(updatedReleases)}
      }
    })
    return updated!
  },
}))

async function readJsonOrThrow(response: Response): Promise<unknown> {
  if (response.ok) {
    return response.json()
  }
  let serverMessage: string | undefined
  try {
    const body = await response.json() as {error?: string}
    serverMessage = body?.error
  } catch {
    // Body wasn't JSON; fall through to statusText.
  }
  throw new Error(serverMessage || response.statusText || `Request failed with status ${response.status}`)
}

function sortReleases(releases: Release[]): Release[] {
  return [...releases].sort((a, b) => {
    if (!!a.archived !== !!b.archived) {
      return a.archived ? 1 : -1
    }
    const aTime = a.targetDate?.getTime()
    const bTime = b.targetDate?.getTime()
    if (aTime !== bTime) {
      if (aTime === undefined) return 1
      if (bTime === undefined) return -1
      return aTime - bTime
    }
    return (a.name || a.id).localeCompare(b.name || b.id)
  })
}
